import {
    Component,
    Input,
    Output,
    EventEmitter,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    signal,
    computed,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../main/type.map.service";
import { PopoverComponent } from "../modules/s25-popover/popover.component";

/**
 * A reusable emoji picker component that displays emojis in a popover grid.
 * Users can select from categorized emojis (Smileys, Gestures, Objects, Nature, Food, Activity, Travel, Symbols).
 */
@TypeManagerDecorator("s25-ng-emoji-picker")
@Component({
    selector: "s25-ng-emoji-picker",
    template: `
        <div class="emoji-picker-container">
            <div class="emoji-input-group">
                <s25-popover
                    [popoverTemplate]="emojiGridTemplate"
                    [popoverClass]="'emoji-picker-popover'"
                    [openTrigger]="'click'"
                    [closeTrigger]="'click'"
                    [onBody]="true"
                    [placement]="['bottom-start', 'top-start', 'bottom', 'top']"
                    [openDelayMillis]="0"
                    [closeDelayMillis]="0"
                    [disabled]="disabled"
                >
                    <button
                        type="button"
                        class="emoji-picker-button"
                        [class.has-value]="value"
                        [class.no-value]="!value"
                        [disabled]="disabled"
                        [title]="value ? 'Change emoji: ' + value : buttonTitle"
                        [attr.aria-label]="value ? 'Change emoji: ' + value : buttonAriaLabel"
                    >
                        @if (value) {
                            <span class="selected-emoji">{{ value }}</span>
                        } @else {
                            <span class="button-icon">{{ buttonEmoji }}</span>
                            <span class="button-label">{{ placeholder }}</span>
                        }
                    </button>
                </s25-popover>
                @if (clearable && value && !disabled) {
                    <button
                        type="button"
                        class="emoji-clear-button"
                        (click)="clearValue()"
                        [title]="'Remove icon'"
                        [attr.aria-label]="'Remove icon'"
                    >
                        <span class="clear-icon">×</span>
                    </button>
                }
            </div>
        </div>

        <!-- Emoji Grid Popover Template -->
        <ng-template #emojiGridTemplate>
            <div class="emoji-grid-container">
                <div class="emoji-grid-header">
                    <span class="emoji-grid-title">Select an Emoji</span>
                </div>
                <div class="emoji-categories">
                    @for (category of emojiCategories; track category.name) {
                        <button
                            type="button"
                            class="category-button"
                            [class.active]="selectedCategory() === category.name"
                            (click)="selectCategory(category.name)"
                            [title]="category.name"
                        >
                            {{ category.icon }}
                        </button>
                    }
                </div>
                <div class="emoji-grid">
                    @for (emoji of getCurrentCategoryEmojis(); track emoji) {
                        <button type="button" class="emoji-button" (click)="selectEmoji(emoji)" [title]="emoji">
                            {{ emoji }}
                        </button>
                    }
                </div>
            </div>
        </ng-template>
    `,
    styles: `
        .emoji-picker-container {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }

        .emoji-input-group {
            display: flex;
            gap: 0.5rem;
            align-items: stretch;
        }

        .emoji-picker-button {
            flex: 1;
            padding: 0.75rem 1rem;
            border: 1px solid #ddd;
            border-radius: 4px;
            background: white;
            cursor: pointer;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            min-height: 42px;
            font-size: 1rem;
        }

        .emoji-picker-button.has-value {
            justify-content: flex-start;
        }

        .emoji-picker-button.no-value {
            justify-content: flex-start;
            color: #999;
        }

        .emoji-picker-button:hover:not(:disabled) {
            background: #f5f5f5;
            border-color: #2196f3;
        }

        .emoji-picker-button:active:not(:disabled) {
            background: #e8e8e8;
        }

        .emoji-picker-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            background-color: #f5f5f5;
        }

        .selected-emoji {
            font-size: 1.5rem;
            line-height: 1;
        }

        .button-icon {
            font-size: 1.2rem;
            line-height: 1;
        }

        .button-label {
            flex: 1;
            text-align: left;
            font-size: 0.95rem;
        }

        .emoji-picker-button.has-value .button-label {
            color: #333;
            font-weight: 500;
        }

        .emoji-picker-button.no-value .button-label {
            color: #999;
        }

        .emoji-clear-button {
            padding: 0.5rem 0.75rem;
            border: 1px solid #ddd;
            border-radius: 4px;
            background: white;
            cursor: pointer;
            transition: all 0.2s;
            color: #666;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 42px;
            font-size: 0.85rem;
            white-space: nowrap;
        }

        .clear-icon {
            font-size: 1.5rem;
            line-height: 1;
            font-weight: 300;
        }

        .emoji-clear-button:hover {
            background: #ffebee;
            border-color: #ef5350;
            color: #ef5350;
        }

        .emoji-clear-button:active {
            background: #ffcdd2;
        }

        /* Emoji Grid Popover Styles - Scoped to this component */
        /* Override Bootstrap's popover-body padding for emoji picker with higher specificity */
        ::ng-deep .emoji-picker-popover .popover-body {
            padding: 0;
        }

        ::ng-deep .emoji-grid-container {
            background: white;
            border-radius: 8px;
            padding: 0.75rem;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            min-width: 280px;
            max-width: 320px;
        }

        ::ng-deep .emoji-grid-header {
            padding: 0.5rem;
            border-bottom: 1px solid #e5e5e5;
            margin-bottom: 0.75rem;
        }

        ::ng-deep .emoji-grid-title {
            font-weight: 600;
            font-size: 0.9rem;
            color: #333;
        }

        ::ng-deep .emoji-categories {
            display: flex;
            gap: 0.25rem;
            padding: 0.5rem;
            border-bottom: 1px solid #e5e5e5;
            margin-bottom: 0.5rem;
            overflow-x: auto;
        }

        ::ng-deep .category-button {
            padding: 0.5rem;
            border: 1px solid transparent;
            border-radius: 4px;
            background: transparent;
            cursor: pointer;
            font-size: 1.2rem;
            transition: all 0.2s;
            min-width: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        ::ng-deep .category-button:hover {
            background: #f5f5f5;
            border-color: #ddd;
        }

        ::ng-deep .category-button.active {
            background: #e3f2fd;
            border-color: #2196f3;
        }

        ::ng-deep .emoji-grid {
            display: grid;
            grid-template-columns: repeat(8, 1fr);
            gap: 0.25rem;
            max-height: 240px;
            overflow-y: auto;
            padding: 0.25rem;
        }

        ::ng-deep .emoji-button {
            padding: 0.5rem;
            border: 1px solid transparent;
            border-radius: 4px;
            background: transparent;
            cursor: pointer;
            font-size: 1.5rem;
            transition: all 0.15s;
            display: flex;
            align-items: center;
            justify-content: center;
            aspect-ratio: 1;
        }

        ::ng-deep .emoji-button:hover {
            background: #f5f5f5;
            border-color: #ddd;
            transform: scale(1.2);
        }

        ::ng-deep .emoji-button:active {
            transform: scale(1.1);
            background: #e3f2fd;
        }

        /* Responsive */
        @media (max-width: 768px) {
            .emoji-input-group {
                flex-wrap: nowrap;
            }

            .emoji-picker-button {
                min-width: 0;
            }

            ::ng-deep .emoji-grid-container {
                max-width: 280px;
            }

            ::ng-deep .emoji-grid {
                grid-template-columns: repeat(6, 1fr);
            }
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, PopoverComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
})
export class S25EmojiPickerComponent {
    @ViewChild("emojiGridTemplate", { static: true }) emojiGridTemplate!: TemplateRef<any>;

    @Input() value: string = "";
    @Input() placeholder: string = "Select icon";
    @Input() disabled: boolean = false;
    @Input() buttonEmoji: string;
    @Input() buttonTitle: string = "Select an icon";
    @Input() buttonAriaLabel: string = "Open emoji picker";
    @Input() clearable: boolean = true;

    @Output() valueChange = new EventEmitter<string>();
    @Output() emojiSelected = new EventEmitter<string>();
    @Output() cleared = new EventEmitter<void>();

    selectedCategory = signal<string>("Smileys");

    // Emoji categories with commonly used emojis
    emojiCategories = [
        {
            name: "Smileys",
            icon: "😀",
            emojis: [
                "😀",
                "😃",
                "😄",
                "😁",
                "😆",
                "😅",
                "🤣",
                "😂",
                "🙂",
                "🙃",
                "😉",
                "😊",
                "😇",
                "🥰",
                "😍",
                "🤩",
                "😘",
                "😗",
                "😚",
                "😙",
                "🥲",
                "😋",
                "😛",
                "😜",
                "🤪",
                "😝",
                "🤑",
                "🤗",
                "🤭",
                "🤫",
                "🤔",
                "🤐",
            ],
        },
        {
            name: "Gestures",
            icon: "👍",
            emojis: [
                "👋",
                "🤚",
                "🖐️",
                "✋",
                "🖖",
                "👌",
                "🤌",
                "🤏",
                "✌️",
                "🤞",
                "🤟",
                "🤘",
                "🤙",
                "👈",
                "👉",
                "👆",
                "🖕",
                "👇",
                "☝️",
                "👍",
                "👎",
                "✊",
                "👊",
                "🤛",
                "🤜",
                "👏",
                "🙌",
                "👐",
                "🤲",
                "🤝",
                "🙏",
                "✍️",
            ],
        },
        {
            name: "Objects",
            icon: "⚽",
            emojis: [
                "⚽",
                "🏀",
                "🏈",
                "⚾",
                "🥎",
                "🎾",
                "🏐",
                "🏉",
                "🥏",
                "🎱",
                "🪀",
                "🏓",
                "🏸",
                "🏒",
                "🏑",
                "🥍",
                "🏏",
                "⛳",
                "🪁",
                "🏹",
                "🎣",
                "🤿",
                "🥊",
                "🥋",
                "🎽",
                "🛹",
                "🛼",
                "🛷",
                "⛸️",
                "🥌",
                "🎿",
                "⛷️",
            ],
        },
        {
            name: "Nature",
            icon: "🌸",
            emojis: [
                "🌸",
                "💮",
                "🏵️",
                "🌹",
                "🥀",
                "🌺",
                "🌻",
                "🌼",
                "🌷",
                "🌱",
                "🪴",
                "🌲",
                "🌳",
                "🌴",
                "🌵",
                "🌾",
                "🌿",
                "☘️",
                "🍀",
                "🍁",
                "🍂",
                "🍃",
                "🪹",
                "🪺",
                "🍄",
                "🌰",
                "🦀",
                "🦞",
                "🦐",
                "🦑",
                "🐙",
                "🦪",
            ],
        },
        {
            name: "Food",
            icon: "🍕",
            emojis: [
                "🍕",
                "🍔",
                "🍟",
                "🌭",
                "🍿",
                "🧈",
                "🥓",
                "🥚",
                "🍳",
                "🧇",
                "🥞",
                "🧈",
                "🍞",
                "🥐",
                "🥖",
                "🥨",
                "🥯",
                "🥞",
                "🧀",
                "🍖",
                "🍗",
                "🥩",
                "🥙",
                "🌮",
                "🌯",
                "🥗",
                "🥘",
                "🍝",
                "🍜",
                "🍲",
                "🍛",
                "🍣",
            ],
        },
        {
            name: "Activity",
            icon: "🎨",
            emojis: [
                "🎨",
                "🎬",
                "🎤",
                "🎧",
                "🎼",
                "🎹",
                "🥁",
                "🎷",
                "🎺",
                "🎸",
                "🪕",
                "🎻",
                "🎲",
                "♟️",
                "🎯",
                "🎳",
                "🎮",
                "🎰",
                "🧩",
                "🪀",
                "🪁",
                "🎪",
                "🎭",
                "🎟️",
                "🎫",
                "🎖️",
                "🏆",
                "🏅",
                "🥇",
                "🥈",
                "🥉",
                "⚽",
            ],
        },
        {
            name: "Travel",
            icon: "✈️",
            emojis: [
                "✈️",
                "🚀",
                "🛸",
                "🚁",
                "🛶",
                "⛵",
                "🚤",
                "🛥️",
                "🛳️",
                "⛴️",
                "🚢",
                "🚂",
                "🚃",
                "🚄",
                "🚅",
                "🚆",
                "🚇",
                "🚈",
                "🚉",
                "🚊",
                "🚝",
                "🚞",
                "🚋",
                "🚌",
                "🚍",
                "🚎",
                "🚐",
                "🚑",
                "🚒",
                "🚓",
                "🚔",
                "🚕",
            ],
        },
        {
            name: "Symbols",
            icon: "💰",
            emojis: [
                "💰",
                "💵",
                "💴",
                "💶",
                "💷",
                "💸",
                "💳",
                "💎",
                "⚖️",
                "🔧",
                "🔨",
                "⚒️",
                "🛠️",
                "⛏️",
                "🔩",
                "⚙️",
                "🔗",
                "⛓️",
                "📌",
                "📍",
                "✂️",
                "📏",
                "📐",
                "🗂️",
                "🗃️",
                "🗄️",
                "🗑️",
                "🔒",
                "🔓",
                "🔐",
                "🔑",
                "🗝️",
            ],
        },
    ];

    constructor(private cd: ChangeDetectorRef) {}

    selectCategory(category: string): void {
        this.selectedCategory.set(category);
        this.cd.detectChanges();
    }

    getCurrentCategoryEmojis(): string[] {
        const category = this.emojiCategories.find((c) => c.name === this.selectedCategory());
        return category ? category.emojis : this.emojiCategories[0].emojis;
    }

    selectEmoji(emoji: string): void {
        this.value = emoji;
        this.valueChange.emit(this.value);
        this.emojiSelected.emit(this.value);
        this.cd.detectChanges();
    }

    clearValue(): void {
        this.value = "";
        this.valueChange.emit(this.value);
        this.cleared.emit();
        this.cd.detectChanges();
    }
}
